import { computed, ref } from 'vue';
import { refDebounced } from '@vueuse/core';
import { toast } from 'vue-sonner';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import { scopesEqual } from '@/utils/filterUtils';
import {
  pruneToDefinition,
  expandDefinition,
  expandCardFields,
  expandGroupBy,
  definitionDiffersIgnoringFilter,
  isSupportedDefinition,
} from '@/utils/savedViewDefinition';
import {
  hasViewNameConflict as hasViewNameConflictIn,
  type SavedViewRecord,
} from '@/utils/savedViews';
import {
  useSavedViewsQuery,
  useSavedViewQuery,
  useSaveSavedView,
  useUpdateSavedView,
  useDeleteSavedView,
  isOptimisticSavedViewId,
  flattenSavedViewPages,
} from '@/composables/api/useSavedViewsApi';
import { usePublishSharedSavedView } from '@/composables/api/useSharedSavedViewsApi';
import { useSavedViewMigrationRunner } from '@/composables/useSavedViewMigrationRunner';
import { buildShareUrl, copyShareToClipboard } from '@/utils/savedViewSharing';

/**
 * Wiring for saved views, used by the section sidebar (its New saved view
 * dialog and open-view controls), by `?view=` routing on lists and on pages such
 * as Track matching, and by the shared-views browser.
 * Centralises the active-view / modified / list state plus the handlers for
 * save / update / discard / rename / delete / select / share, so the callers
 * don't each re-implement them.
 *
 * Saved views are SERVER rows as of SAU-310. What changed, and what did not:
 *
 * - The LIST comes from TanStack Query, not localStorage, so views follow the
 *   user across browsers. Every write is a mutation with an optimistic patch.
 * - Applying a view is still SYNCHRONOUS and local — it writes the live column
 *   and filter state, no round-trip. TrackMatchingPage wraps that call in
 *   `runWithUnsavedChangesGuard`, and handing that guard a promise would let it
 *   discard pending track-match edits before the guard resolved.
 * - `hasNameConflict` is still SYNCHRONOUS, checked against the cached list.
 *   The New saved view dialog calls it on every keystroke to enable its Save
 *   button; a promise there is truthy, so every name
 *   would read as a conflict and the button would never enable. It is a HINT and
 *   not the authority, and since the list became paged it is a weaker one: a name
 *   used by a view on a page the menu has not loaded, or one filtered out by the
 *   search box, reads as free here. The server decides the real answer with the
 *   unique index — which folds accents and trailing spaces in ways no client-side
 *   check reproduces anyway — and refuses with a 409 the user can read.
 *
 * TrackMatchingTableHeader overrides only the `select-view` handler so its
 * unsaved-changes guard can interpose; everything else uses these defaults.
 */
export function useSavedViews() {
  const store = usePersistentTableStore();

  const entityId = computed(() => store.storeKey);

  /**
   * What the user has typed into the menu's search box.
   *
   * Server-side, because the list is one page at a time now: filtering only the
   * rows already loaded would search the first hundred views and silently miss the
   * rest, which is worse than not offering the box at all.
   *
   * Same debounce shape as the library dialog — the query keys on the DEBOUNCED
   * term so typing does not fire one GET per keystroke, but CLEARING is immediate,
   * because a debounced clear leaves the previous term's "no views match" on screen
   * for a quarter of a second and then refetches that stale term first.
   */
  const search = ref('');
  const debouncedSearch = refDebounced(search, 250);
  const queryTerm = computed(() => (search.value === '' ? '' : debouncedSearch.value));

  const savedViewsQuery = useSavedViewsQuery(entityId, queryTerm);
  const saveMutation = useSaveSavedView();
  const updateMutation = useUpdateSavedView();
  const deleteMutation = useDeleteSavedView();
  const publishMutation = usePublishSharedSavedView();

  /**
   * True while a share POST is in flight.
   *
   * Internal. It was returned from this composable for a Share button to disable
   * itself with, and no component ever read it — so a second share during the first
   * was refused by a silent early return, indistinguishable from a dead key. The
   * refusal says so now, at the point it happens.
   */
  const isSharing = ref(false);

  const views = computed<SavedViewRecord[]>(() =>
    flattenSavedViewPages(savedViewsQuery.data.value),
  );

  // Carries any localStorage views onto the server, once, on the first query that
  // actually succeeds. Guarded module-side, so the four headers and the page all
  // calling this composable still produce exactly one migration per page load.
  useSavedViewMigrationRunner(computed(() => savedViewsQuery.isSuccess.value));

  const activeSavedViewId = computed<number | null>(() => store.activeSavedViewId);

  /** The active view IF the pages currently loaded happen to contain it. */
  const activeViewInList = computed<SavedViewRecord | null>(() => {
    const id = activeSavedViewId.value;
    if (id === null) {
      return null;
    }
    return views.value.find((view) => view.id === id) ?? null;
  });

  /**
   * The active view fetched by id, for when the loaded pages do not have it.
   *
   * Only enabled once the list has actually settled AND come back without the row,
   * so the ordinary case — your active view is on the first page, where a view you
   * are working in almost always is — costs no request at all.
   */
  const pinnedActiveViewQuery = useSavedViewQuery(
    activeSavedViewId,
    computed(
      () =>
        activeSavedViewId.value !== null &&
        activeViewInList.value === null &&
        savedViewsQuery.isSuccess.value,
    ),
  );

  /**
   * The view the user is working in.
   *
   * Resolved from the loaded pages first and fetched by id only as a fallback. That
   * fallback is what the paged listing made necessary: this used to be a lookup in a
   * list that held every view you owned, and an id that did not resolve meant the row
   * was genuinely gone. Now it much more often means the row is simply on a page the
   * menu has not loaded, or is filtered out by the search box — and treating that as
   * "no active view" makes the primary button read "Save", drops the dirty dot, and
   * turns the next press into a second view rather than an update.
   */
  const activeView = computed<SavedViewRecord | null>(
    () => activeViewInList.value ?? pinnedActiveViewQuery.data.value ?? null,
  );

  /** The live table state, pruned into the same shape a stored view carries. */
  const liveDefinition = computed(() =>
    pruneToDefinition(
      entityId.value,
      store.buildColumnConfigSnapshotFromState(),
      store.filter,
      store.buildLayoutSnapshot(),
      store.buildCardFieldsSnapshot(),
      store.groupBy,
    ),
  );

  /**
   * True when the live filter + columns differ from the active view. Drives the
   * "Update {name}" primary label and the amber dirty-dot on the active row.
   *
   * Compares PRUNED definitions, not raw column maps — live state carries
   * incidental entries a canonical expansion never reproduces, so comparing the
   * maps would make every freshly loaded view read as permanently dirty.
   */
  const isModified = computed<boolean>(() => {
    const active = activeView.value;
    if (!active || !isSupportedDefinition(active.definition)) {
      return false;
    }
    if (!scopesEqual(store.filter.ruleScopes, active.definition.filter.ruleScopes)) {
      return true;
    }
    // The stored side is normalised to the CURRENT universe first, so both sides
    // are describing the same set of columns, and a missing card list is read as
    // the default card fields. See definitionDiffersIgnoringFilter.
    return definitionDiffersIgnoringFilter(entityId.value, liveDefinition.value, active.definition);
  });

  /**
   * Apply a stored view to the live table state. Synchronous on purpose.
   *
   * Returns false when the view was refused, so the caller does not report a
   * success it did not get.
   */
  function applyView(view: SavedViewRecord): boolean {
    if (!isSupportedDefinition(view.definition)) {
      // A definition written by a newer deploy. Say so rather than applying
      // half of it — a partial layout is worse than none.
      toast.error(`"${view.name}" was saved by a newer version and cannot be loaded here.`);
      return false;
    }

    // One write path, shared with the import flow. This used to be nine writes
    // spelled out here beside nine equivalents in the store, and the branch paid
    // for the duplication twice: layout capture and then card capture each had to
    // be added to both, and the two drifted while that happened.
    //
    // deepClone, NOT structuredClone, inside the store. vue-query hands back
    // `readonly(reactive(...))`, and structuredClone throws DataCloneError on a
    // Proxy — which killed this function before it wrote a single line of state, so
    // clicking a saved view did nothing at all: no view applied, no error, no toast.
    store.applyDefinitionToScope(
      entityId.value,
      view.definition.filter,
      expandDefinition(entityId.value, view.definition),
      view.definition.layout,
      view.id,
      // A view with no card list puts the default card fields back.
      expandCardFields(entityId.value, view.definition),
      // A view saved before grouping was stored opens with no grouping.
      expandGroupBy(entityId.value, view.definition),
    );

    return true;
  }

  const handlers = {
    onSaveNew(name: string) {
      const trimmed = name.trim();
      if (trimmed === '') {
        toast.error('Enter a name for the view.');
        return;
      }
      if (hasViewNameConflictIn(views.value, trimmed)) {
        toast.error(`A saved view named "${trimmed}" already exists.`);
        return;
      }
      // Captured, not read again in the callback. The store's `activeSavedViewId`
      // setter resolves the scope when it runs, so a save that settles after the
      // user has switched the entity picker would stamp this view's id onto the
      // page they moved to — and leave the page it belongs to still saying "Save".
      const targetEntityId = entityId.value;
      // The term is captured alongside the entity for the same reason: the
      // optimistic patch has to land on the list the user is looking at, and both
      // can change while the request is in flight.
      const targetSearch = queryTerm.value;
      saveMutation.mutate(
        {
          entity: targetEntityId,
          search: targetSearch,
          name: trimmed,
          definition: liveDefinition.value,
        },
        {
          onSuccess: (saved) => {
            store.setActiveSavedViewIdForScope(targetEntityId, saved.id);
            toast.success(`Saved view "${saved.name}".`);
          },
        },
      );
    },
    onUpdateActive() {
      const active = activeView.value;
      if (!active || isOptimisticSavedViewId(active.id)) {
        return;
      }
      updateMutation.mutate(
        {
          entity: entityId.value,
          search: queryTerm.value,
          savedViewId: active.id,
          name: active.name,
          definition: liveDefinition.value,
        },
        { onSuccess: () => toast.success('View updated.') },
      );
    },
    onDiscardChanges() {
      const active = activeView.value;
      if (!active) {
        return;
      }
      if (!applyView(active)) {
        return;
      }
      toast.success(`Reverted to "${active.name}".`);
    },
    /**
     * Stop treating the live table as the active view, and CHANGE NOTHING ELSE.
     *
     * The third thing you can want to do with a view you have been editing, and
     * the one that was missing. "Update" writes your changes into it and
     * "Discard changes" throws them away to get back to it — both assume the work
     * on screen is still about that view. Once it is not, neither is what you
     * want: the primary button goes on reading "Update \"Green zones\"" over work
     * that has nothing to do with Green zones, and the only exits are to
     * overwrite a view you did not mean to touch or to lose what you have done.
     *
     * So the filters, columns and layout are deliberately left exactly as they
     * are. This is a statement about the LABEL, not about the table — and what is
     * on screen is precisely what the user is about to save under a new name,
     * which the primary button now offers, because with nothing selected it
     * reads "Save" again.
     */
    onClearActive() {
      if (store.activeSavedViewId === null) {
        return;
      }
      // Scoped, like every other write here: this settles synchronously, but the
      // setter resolves the scope when it runs and the entity picker is one
      // click away.
      store.setActiveSavedViewIdForScope(entityId.value, null);
      toast.success('No view selected — your filters and columns are unchanged.');
    },
    onRename(payload: { id: number; name: string }) {
      const view = views.value.find((candidate) => candidate.id === payload.id);
      if (!view || isOptimisticSavedViewId(view.id)) {
        return;
      }
      const trimmed = payload.name.trim();
      if (trimmed === '') {
        toast.error('Enter a name for the view.');
        return;
      }
      if (trimmed === view.name) {
        return;
      }
      if (hasViewNameConflictIn(views.value, trimmed, view.id)) {
        toast.error(`A saved view named "${trimmed}" already exists.`);
        return;
      }
      // Rename does NOT re-snapshot: the stored definition travels unchanged.
      updateMutation.mutate({
        entity: entityId.value,
        search: queryTerm.value,
        savedViewId: view.id,
        name: trimmed,
        definition: view.definition,
      });
    },
    onDelete(id: number) {
      if (isOptimisticSavedViewId(id)) {
        return;
      }
      // Cleared optimistically so the checkmark goes with the row, and restored
      // if the delete fails. The mutation puts the ROW back on error; without
      // this the row returns unselected and the control quietly forgets which
      // view the user was working in.
      // Same scope capture as onSaveNew: the rollback runs asynchronously, so it
      // must name the entity it is restoring rather than whichever one is routed
      // by the time the delete fails.
      const targetEntityId = entityId.value;
      const targetSearch = queryTerm.value;
      const wasActive = store.activeSavedViewId === id;
      if (wasActive) {
        store.setActiveSavedViewIdForScope(targetEntityId, null);
      }
      deleteMutation.mutate(
        { entity: targetEntityId, search: targetSearch, savedViewId: id },
        {
          onError: () => {
            if (wasActive) {
              store.setActiveSavedViewIdForScope(targetEntityId, id);
            }
          },
        },
      );
    },
    /**
     * Apply the view the user clicked.
     *
     * Takes the RECORD rather than an id, and does no lookup. It used to search
     * `views` — this instance's cached page — which is not necessarily the list
     * the click came from: Track Matching mounts one of these on the page and
     * another in its header, each with its own search term and so its own cached
     * results. A view found through the header's search but past the page
     * instance's first hundred unfiltered rows was simply not there, and the
     * click did nothing at all — no view, no error, no toast.
     */
    onSelectView(view: SavedViewRecord) {
      // applyView has already said why if it refused. Reporting a load on top of
      // that told the user in one breath that the view could not be loaded and
      // that it was loaded.
      if (!applyView(view)) {
        return;
      }
      toast.success(`Loaded view "${view.name}".`);
    },
    /**
     * Publish a view to the library and copy its short link.
     *
     * The link is a share KEY the server mints, so there is nothing to build
     * client-side and nothing that can grow with the view's complexity — the
     * whole reason SAU-310 exists.
     *
     * If the clipboard write fails after a successful publish, the row STAYS
     * published: the user asked to share, and a clipboard permission failure is
     * not a retraction. They are told to copy it by hand instead.
     */
    async onShare(id: number) {
      const view = views.value.find((candidate) => candidate.id === id);
      if (!view || isOptimisticSavedViewId(view.id)) {
        return;
      }
      if (isSharing.value) {
        // Said, not swallowed. `isSharing` was returned from this composable and
        // wired to nothing, so the share button was never disabled — a second
        // press while the first publish was in flight did nothing at all and
        // looked exactly like a dead key.
        toast.error('Still sharing the last view — try again in a moment.');
        return;
      }
      isSharing.value = true;

      // The clipboard write is STARTED here, synchronously, inside the click —
      // before the publish request is awaited. Safari refuses a write that
      // begins after a network await, so the old publish-then-copy order left
      // every Safari share with a library row and an empty clipboard. The URL is
      // handed over as a promise and the blobs resolve when the key arrives.
      const published = publishMutation.mutateAsync({
        entity: entityId.value,
        savedViewId: view.id,
      });
      const url = published.then((row) => buildShareUrl(window.location.origin, row.share_key));
      const copied = copyShareToClipboard(view.name, url);
      // If the publish fails the copy rejects too; it is dealt with below on the
      // success path and must not surface as an unhandled rejection otherwise.
      copied.catch(() => {});

      try {
        await published;
      } catch {
        // publishMutation.onError already toasted the failure.
        isSharing.value = false;
        return;
      }

      try {
        await copied;
        toast.success('Share link copied — paste it into Slack to share.');
      } catch {
        toast.error(`Shared, but the link could not be copied. It is: ${await url}`);
      } finally {
        isSharing.value = false;
      }
    },
  };

  return {
    activeView,
    isModified,
    views,
    /** Two-way bound to the menu's search box. */
    search,
    /**
     * Whether the server said there is another page, and how to ask for it.
     *
     * `isFetchingNextPage` is separate from the query's own `isLoading` on purpose:
     * loading MORE must not blank the rows already on screen.
     */
    hasMore: computed(() => savedViewsQuery.hasNextPage.value),
    isLoadingMore: computed(() => savedViewsQuery.isFetchingNextPage.value),
    loadMore: () => savedViewsQuery.fetchNextPage(),
    isError: computed(() => savedViewsQuery.isError.value),
    /** Synchronous by contract — see the note at the top of this file. */
    hasViewNameConflict: (name: string, excludeId?: number) =>
      hasViewNameConflictIn(views.value, name, excludeId),
    // `applyView` and an `isLoading` passthrough used to be returned here and no
    // component read either. `applyView` being reachable from outside is what let
    // the double-toast survive: the test for the refusal path called it directly,
    // so it never went through the handler that then reported success anyway.
    handlers,
  };
}
